<template>
  <div class="game-tracker">
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row d-flex justify-content-center" :class="{ 'gt-display-active': showDisplayPanel }">
      <div :class="showDisplayPanel ? 'col-12' : 'col-lg-11'">
        <simple-wizard :show-header="false" :show-next-button="show_next_button" ref="wiz" :finish-button-text="submitted ? 'View results' : 'Submit'"
          @tab-change="onTabChange"
        >
          <template v-slot:footer-right>
            <base-button
              v-if="isObjectivesStep && gt_settings.show_complete_button"
              type="primary"
              class="complete-game-footer-btn"
              @click.native="handleCompleteGameFromFooter()"
            >
              <i class="tim-icons icon-check-2 mr-2"></i>
              Complete event now
            </base-button>
          </template>
          <wizard-tab :before-change="() => validateStep('step0')">
            <template slot="label">
              <i class="tim-icons icon-shape-star"></i>
              <p class="d-none d-sm-block">Start</p>
            </template>
            <div class="row justify-content-center" style="margin-bottom: 24px;">
              <!-- Mobile -->
              <div class="col d-sm-none d-block">
                <div class="start-actions">
                  <base-button v-if="!view_user_drafts" class="start-actions__new" v-on:click="createNewDraft()">
                    <i class="tim-icons el-icon-plus mr-1"></i> New form
                  </base-button>
                  <base-button v-if="!view_user_drafts && hasPermissions('game_history_view') && getPermissionLevel('game_history_view') === 2"
                    class="start-actions__secondary" link v-on:click="getAllDrafts()">
                    View all user drafts
                  </base-button>
                  <base-button v-if="view_user_drafts && hasPermissions('game_history_view')"
                    class="start-actions__secondary" link v-on:click="view_user_drafts = false">
                    Return to my drafts
                  </base-button>
                </div>
              </div>
              <!-- Desktop -->
              <div class="col d-sm-block d-none">
                <div class="start-actions">
                  <base-button v-if="!view_user_drafts" class="start-actions__new" v-on:click="createNewDraft()">
                    <i class="tim-icons el-icon-plus mr-1"></i> New form
                  </base-button>
                  <base-button v-if="!view_user_drafts && hasPermissions('game_history_view') && getPermissionLevel('game_history_view') === 2"
                    class="start-actions__secondary" link v-on:click="getAllDrafts()">
                    View all user drafts
                  </base-button>
                  <base-button v-if="view_user_drafts && hasPermissions('game_history_view')"
                    class="start-actions__secondary" link v-on:click="view_user_drafts = false">
                    Return to my drafts
                  </base-button>
                </div>
              </div>
            </div>
            <div class="row justify-content-center" v-if="view_user_drafts && user_drafts.length <=0">
              <div class="col-11">
                <div class="mt-3">
                  <div class="font-icon-detail">
                    <span style="font-size: 3em">
                      <i class="fas fa-exclamation-triangle"></i>
                    </span>
                    <h4 class="mt-3">No drafts have been created.</h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="row justify-content-center" v-if="view_user_drafts && user_drafts.length >0">
              <div class="col-lg-8 col-md-10 col-12">
                <div class="draft-list">
                  <div v-for="(draft, index) in user_drafts" :key="'ud-' + draft.id"
                       class="draft-item gt-list-item">
                    <img v-if="draft.game_image_url" v-twin-src="draft.game_image_url" class="draft-item__img" alt="" />
                    <div class="draft-item__img-placeholder" v-else>
                      <i class="tim-icons icon-shape-star"></i>
                    </div>
                    <div class="draft-item__content">
                      <div class="draft-item__header">
                        <span class="draft-item__name">
                          {{ draft.game_name || 'Empty draft' }}
                        </span>
                        <div class="draft-item__actions">
                          <button v-if="!editing[index] && canEditDraft(draft)"
                            @click="editDraft(draft, index)"
                            class="draft-item__action-btn">
                            <i class="tim-icons icon-pencil"></i>
                          </button>
                          <button v-if="editing[index]"
                            @click="saveDraft(draft, index)"
                            class="draft-item__action-btn draft-item__action-btn--save">
                            <i class="tim-icons icon-check-2"></i>
                          </button>
                          <button v-if="canDeleteDraft(draft)"
                            @click="deleteDraftPrompt(draft)"
                            class="draft-item__delete">
                            <i class="tim-icons icon-simple-remove"></i>
                          </button>
                        </div>
                      </div>
                      <div class="draft-item__meta">
                        <span v-if="draft.booking_date" class="draft-item__meta-tag text-white">
                          <i class="tim-icons icon-calendar-60"></i>
                          {{ getFormattedDate(draft.booking_date) }}, {{ getTime(draft.start_time) }}
                        </span>
                        <span class="draft-item__meta-tag text-white">
                          <i class="tim-icons icon-watch-time"></i>
                          Created {{ getLocalDate(draft.time_created) }}
                        </span>
                        <span v-if="draft.time_updated" class="draft-item__meta-tag text-white">
                          <i class="tim-icons icon-refresh-02"></i>
                          Updated {{ getLocalDate(draft.time_updated) }}
                        </span>
                      </div>
                      <!-- Game master info -->
                      <div class="draft-item__gm">
                        <div v-if="!editing[index]">
                          <span class="draft-item__meta-tag text-white">
                            <i class="tim-icons icon-single-02"></i>
                            {{ draft.gm_first_name }} {{ draft.gm_last_name }}
                            <span style="opacity: 0.5; margin-left: 4px;">{{ draft.gm_email }}</span>
                          </span>
                        </div>
                        <div v-else style="max-width: 350px;">
                          <base-input>
                            <el-select
                              v-model="selectedUser"
                              class="select-primary"
                              placeholder="User"
                              value-key="id"
                            >
                              <el-option
                                v-for="(c, i) in company_users"
                                class="select-primary"
                                :label="c.first_name + ' ' + c.last_name + ' (' + c.email + ')'"
                                :value="{id: c.id, email: c.email}"
                                :key="'C' + i"
                              ></el-option>
                            </el-select>
                          </base-input>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <start-new-form v-if="!view_user_drafts" @on-get-drafts="onGetDrafts"
              ref="step0"
              :game-instance-id="gameInstanceId"
              @on-validated="onStepValidated"
              @draft-clicked="onDraftClicked"
              @draft-selected="onDraftSelected"
              @update:game-instance-id="updateGameInstanceId"
            ></start-new-form>
          </wizard-tab>

          <wizard-tab :before-change="() => validateStep('step1')">
            <template slot="label">
              <i class="tim-icons icon-calendar-60"></i>
              <p class="d-none d-sm-block">Booking</p>
            </template>
            <booking-info-step
              ref="step1"
              :game-instance-id="gameInstanceId"
              @on-validated="onStepValidated"
            ></booking-info-step>
          </wizard-tab>

          <wizard-tab :before-change="() => validateStep('step2')">
            <template slot="label">
              <i class="tim-icons icon-single-02"></i>
              <p class="d-none d-sm-block">Group</p>
            </template>
            <pre-game-notes-step
              ref="step2"
              :game-instance-id="gameInstanceId"
              @on-validated="onStepValidated"
            ></pre-game-notes-step>
          </wizard-tab>

          <wizard-tab v-if="this.hasFieldEnabled(1)" :before-change="() => validateStep('step3')">
            <template slot="label">
              <i class="tim-icons icon-bullet-list-67"></i>
              <p class="d-none d-sm-block">Objectives</p>
            </template>

            <!-- Display mode OFF: full-width puzzle step -->
            <puzzle-step v-if="!gt_settings.enable_in_room_display"
              ref="step3"
              :game-instance-id="gameInstanceId"
              @on-validated="onStepValidated"
              @complete-game="handleCompleteGame"
              :show-complete-button="gt_settings.show_complete_button"
              :display-settings="gt_settings"
              :game-id="currentGameId" />

            <!-- Display mode ON: sidebar widgets + objectives + optional mirror -->
            <div v-else class="row gt-display-row">
              <div :class="gt_settings.display_mirror_view ? 'col-lg-2' : 'col-lg-3'" class="gt-sidebar">
                <display-control-panel ref="displayPanel"
                  :socket="socket"
                  :game-config="displayConfig || {}"
                  :game-id="currentGameId"
                  :display-settings="gt_settings"
                  :visible-widgets="gt_settings.display_mirror_view ? ['connection', 'timer', 'music', 'onscreen'] : null" />
              </div>
              <div :class="gt_settings.display_mirror_view ? 'col-lg-6' : 'col-lg-9'">
                <puzzle-step ref="step3"
                  :game-instance-id="gameInstanceId"
                  @on-validated="onStepValidated"
                  @complete-game="handleCompleteGame"
                  @hint-sent="handlePuzzleHintSent"
                  :enable-in-room-display="true"
                  :show-complete-button="gt_settings.show_complete_button"
                  :display-settings="gt_settings"
                  :socket="socket"
                  :game-config="displayConfig || {}"
                  :game-id="currentGameId" />
              </div>
              <div v-if="gt_settings.display_mirror_view" class="col-lg-4 gt-mirror-col">
                <div class="mirror-display-panel">
                  <div class="mirror-display-panel__header">
                    <span class="mirror-display-panel__label">
                      <i class="tim-icons icon-tv-2 mr-1"></i>
                      Customer screen mirror
                    </span>
                  </div>
                  <div class="mirror-display-panel__body" ref="mirrorBody">
                    <div v-if="!mirrorReady" class="mirror-display-panel__loading">
                      <i class="tim-icons icon-tv-2"></i>
                      <span>Loading display...</span>
                    </div>
                    <iframe
                      v-show="mirrorReady"
                      :src="mirrorDisplayUrl"
                      class="mirror-display-panel__iframe"
                      :style="mirrorIframeStyle"
                      frameborder="0"
                      allowfullscreen
                      @load="onMirrorIframeLoad"
                    ></iframe>
                  </div>
                </div>
                <display-control-panel ref="displayPanelSecondary"
                  :socket="socket"
                  :game-config="displayConfig || {}"
                  :game-id="currentGameId"
                  :display-settings="gt_settings"
                  :visible-widgets="['custom', 'hints']"
                  class="mt-3" />
              </div>
            </div>
          </wizard-tab>

          <wizard-tab :before-change="() => validateStep('step4')">
            <template slot="label">
              <i class="tim-icons icon-notes"></i>
              <p class="d-none d-sm-block">Notes</p>
            </template>
            <post-game-notes-step :game-instance-id="gameInstanceId" @on-validated="onStepValidated" ref="step4"></post-game-notes-step>
          </wizard-tab>

          <wizard-tab v-if="this.hasFieldEnabled(2)" :before-change="() => validateStep('step5')">
            <template slot="label">
              <i class="tim-icons icon-camera-18"></i>
              <p class="d-none d-sm-block">Photos</p>
            </template>
            <photo-step ref="step5" :game-instance-id="gameInstanceId" @on-validated="onStepValidated"></photo-step>
          </wizard-tab>

          <wizard-tab v-if="gt_settings.include_review_step" :before-change="() => wizardComplete()">
            <template slot="label">
              <i class="tim-icons icon-check-2"></i>
              <p class="d-none d-sm-block">Review</p>
            </template>
            <review-step :game-instance-id="gameInstanceId" @on-validated="onStepValidated" ref="step6"></review-step>
          </wizard-tab>

        </simple-wizard>
      </div>
    </div>

    <base-alert-dialog :show="alertDialog.show"
                       :title="alertDialog.title"
                       :message="alertDialog.message"
                       :variant="alertDialog.variant"
                       :show-cancel="alertDialog.showCancel"
                       :confirm-text="alertDialog.confirmText"
                       :cancel-text="alertDialog.cancelText"
                       :confirm-variant="alertDialog.confirmVariant"
                       @confirm="resolveAlert(true)"
                       @cancel="resolveAlert(false)" />
  </div>
</template>

<script>
  import BookingInfoStep from './BookingInfoStep'
  import PreGameNotesStep from './PreGameNoteStep'
  import PuzzleStep from "./PuzzleStep";
  import PostGameNotesStep from "./PostGameNotesStep"
  import PhotoStep from "@/pages/Games/GameTracker/PhotoStep";
  import ReviewStep from "./ReviewStep"
  import DisplayControlPanel from "./DisplayControlPanel";
  import swal from 'sweetalert2'
  import { SimpleWizard, WizardTab } from 'src/components'
  import { Auth } from 'aws-amplify'
  import { API_LOCATION } from '../../../api-config'
  import StartNewForm from "@/pages/Games/GameTracker/StartNewForm";
  const axios = require('axios')
  import {formattedDate, localDate, localTime, time} from "@/plugins/dateFormatter";
  import {formatGroupDate} from "@/plugins/dates";

  const NO_RECIPIENTS_MESSAGE = 'Photos are emailed to the players on this booking who signed a waiver with an email address, and none of them has one. Add an email to a player, or assign the photo to a customer on the Photos page and send it from there.'
// True when the photo email endpoint rejected the send because nobody on the
// booking has an email address (400 with error 'No valid recipients').
function isNoRecipientsError(err) {
  if (!err || !err.response || err.response.status !== 400) return false
  let data = err.response.data
  if (typeof data === 'string') { try { data = JSON.parse(data) } catch (e) { return false } }
  return !!(data && data.error === 'No valid recipients')
}

export default {
      name: "GameTracker",
      components: {
        StartNewForm,
        PuzzleStep,
        BookingInfoStep,
        PreGameNotesStep,
        PostGameNotesStep,
        PhotoStep,
        ReviewStep,
        DisplayControlPanel,
        SimpleWizard,
        WizardTab
      },
      data() {
        return {
          alertDialog: { show: false, title: '', message: '', variant: 'warning', showCancel: false, confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary' },
          alertResolve: null,
          loading: false,
          gameInstanceId: null,
          date_format: JSON.parse(localStorage.getItem('group')).date_format,
          time_format: JSON.parse(localStorage.getItem('group')).time_format,
          show_next_button: true,
          gt_settings: '',
          optional_fields: [],
          user_drafts: [],
          company_users: [],
          editing: [],
          selectedUser: '',
          lastSelectedDraft: null,
          // Background draft save fired when leaving Group or Notes; every
          // draft-list refetch awaits it so the server copy is never older
          // than what the user just typed.
          pendingDraftSave: null,
          selectingDraft: false,
          queuedDraftClick: null,
          view_user_drafts: false,
          fields: [
            { key: 'game', label: 'Event' },
            'created',
            'last_updated',
            { key: 'game_master', label: 'Event host' },
            'actions'
          ],

          // In-room display state
          isObjectivesStep: false,
          showDisplayPanel: false,
          socket: null,
          displayConfig: null,
          currentGameId: null,
          isDestroying: false,
          wsReconnectAttempts: 0,
          mirrorScale: 0.2,
          mirrorReady: false,
          displayTimerValue: null,
          submitted: false,
          completedInstanceId: null,
        }
      },
      computed: {
        mirrorDisplayUrl() {
          if (!this.currentGameId) return ''
          return window.location.origin + '/clues/display?gameId=' + this.currentGameId
        },
        mirrorIframeStyle() {
          return {
            width: '1920px',
            height: '1080px',
            transform: 'scale(' + this.mirrorScale + ')',
            transformOrigin: '0 0'
          }
        }
      },
      watch: {
        showDisplayPanel(val) {
          if (val && this.gt_settings.display_mirror_view) {
            this.mirrorReady = false
            setTimeout(() => {
              this.updateMirrorScale()
              this.mirrorReady = true
            }, 2000)
          }
        }
      },
      methods: {
        // Promise-based wrapper around the shared base-alert-dialog. Resolves true
        // on confirm, false on cancel or dismiss.
        showAlert(opts){
          if(this.alertResolve){
            this.alertResolve(false)
          }
          this.alertDialog = {
            show: true,
            title: opts.title || '',
            message: opts.message || '',
            variant: opts.variant || 'warning',
            showCancel: opts.showCancel === true,
            confirmText: opts.confirmText || 'OK',
            cancelText: opts.cancelText || 'Cancel',
            confirmVariant: opts.confirmVariant || 'primary'
          }
          return new Promise(resolve => { this.alertResolve = resolve })
        },
        resolveAlert(value){
          this.alertDialog.show = false
          const resolve = this.alertResolve
          this.alertResolve = null
          if(resolve) resolve(value)
        },
        isOwnDraft(draft){
          let profile = JSON.parse(localStorage.getItem('profile'))
          return profile && draft.gm_id === profile.id
        },
        getPermissionLevel(scope){
          let levels = JSON.parse(localStorage.getItem('permissionLevels') || '{}')
          return levels[scope] !== undefined ? levels[scope] : 2
        },
        canEditDraft(draft){
          if (!this.hasPermissions('game_history_update')) return false
          if (this.getPermissionLevel('game_history_update') === 2) return true
          return this.isOwnDraft(draft)
        },
        canDeleteDraft(draft){
          if (this.hasPermissions('game_history_delete')) return true
          return this.isOwnDraft(draft)
        },
        hasPermissions(scope){
          let permissions = JSON.parse(localStorage.getItem('permissions'))
          const matchFound = permissions.some(element => element === scope);
          return matchFound
        },
        editDraft(item, index){
          this.selectedUser = {
            email: item.gm_email,
            id: item.gm_id
          }
          this.$set(this.editing, index, true);
        },
        async saveDraft(item, index){
          this.loading = true
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let companyId = localStorage.getItem('companyId')
          let url = API_LOCATION + 'companies/' + companyId + '/games/instances/drafts/all'
          let data = {
            game_master_id: this.selectedUser.id,
            id: item.id
          }

          axios.put(url, data, config)
            .then(response => {
              swal({
                title: 'Success',
                text: "The draft has been updated.",
                type: 'success'
              })
              this.getAllDrafts()
              this.loading = false
            })
            .catch(err => {
              this.loading = false
              if(err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else {
                swal('Error!', 'Something went wrong', 'error')
              }
            })
          this.$set(this.editing, index, false);
        },
        getAuthToken() {
          return new Promise ( function(resolve) {
            Auth.currentSession()
              .then(data =>{
                resolve(data.idToken.jwtToken)
              })
              .catch(err => console.log(err))
          })
        },
        async validateStep(ref) {
          window.scrollTo(0,0)

          if(ref === 'step0'){
            await this.$nextTick() // wait for gameInstanceId prop to propagate to children
            await this.$refs['step1'].getBookings(0)
          }
          else if(ref === 'step1'){
            this.$refs['step2'].setOptionalFields(this.optional_fields)
            let isEmpty = this.$refs['step1'].isEmptyBooking()
            if(isEmpty){
              return
            }
            let validBooking = await this.$refs['step1'].setBookingData()
            if(!validBooking){
              return
            }

            if (!this.$refs['step2'].draftPopulated) {
              let gameId = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo'))).booking.game_id
              let bookingId = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo'))).booking.id
              await this.$refs['step2'].getUsers(gameId, bookingId)
              this.$refs['step2'].populateDraft()
            }
          }
          else if(ref === 'step2'){
            // Validate before setting up the next step
            let isValid = await this.$refs['step2'].validate()
            if (!isValid) return false

            this.$refs['step4'].setOptionalFields(this.optional_fields)
            if(this.hasFieldEnabled(1)) {
              let bookingInfo = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo')))
              let gameId = bookingInfo.booking.game_id
              this.currentGameId = gameId

              // Fetch display configuration for timer settings
              await this.fetchDisplayConfig(gameId)

              // Initialize in-room display if enabled
              if (this.gt_settings.enable_in_room_display) {
                this.isDestroying = false
                this.connectWebSocket(gameId)

                // Initialize display panel timer after Vue re-renders
                this.$nextTick(() => {
                  if (this.$refs.displayPanel && this.displayConfig) {
                    this.$refs.displayPanel.initTimer(
                      this.displayConfig.max_time,
                      'down'
                    )
                  }
                  // Initialize secondary panel's hint counter when mirror view is on
                  if (this.$refs.displayPanelSecondary && this.displayConfig) {
                    this.$refs.displayPanelSecondary.initTimer(
                      this.displayConfig.max_time,
                      'down'
                    )
                  }
                })
              }

              // Set timer settings for PuzzleStep (hidden when display mode is on, but still needed internally)
              const timer_value = JSON.parse(localStorage.getItem(this.draftKey('draft'))) ? JSON.parse(localStorage.getItem(this.draftKey('draft'))).timer_value : null
              this.$refs['step3'].setTimerSettings(this.gt_settings, timer_value,
                bookingInfo.booking.duration_minutes
              )
              await this.$refs['step3'].createPuzzles()
              if (this.gt_settings.pacing_enabled) {
                const pacingStart = this.$refs['step2'] ? this.$refs['step2'].model.start_time : null
                this.$refs['step3'].initPacing(pacingStart instanceof Date ? pacingStart : null, bookingInfo.booking.duration_minutes)
              }
            }
            return true
          }
          else if(ref === 'step3'){
            await this.$refs['step3'].setDraft(this.captureDisplayTimer())
            this.$refs['step3'].stopTimer()
            // Only populate from draft on first visit; on return visits the
            // component model already holds the user's edits (v-show keeps it mounted)
            if (!this.$refs['step4'].draftPopulated) {
              this.$refs['step4'].populateDraft()
            }
          }
          else if(ref === 'step4'){
            this.$refs['step6'].setOptionalFields(this.optional_fields)
            // this.$refs['step4'].convertTime()
            let test = await this.$refs['step4'].setPostNotes()

            if(this.hasFieldEnabled(2)){
              if (!this.$refs['step5'].draftPopulated) {
                this.$refs['step5'].populateDraft()
              }
            } else {
              // this.$refs['step0'].setDraft()
              this.$refs['step6'].getReviewData(this.gt_settings, this.gatherStepData())
            }
          }
          else if (ref === 'step5'){
            this.$refs['step5'].setPhoto()
            this.$refs['step6'].getReviewData(this.gt_settings, this.gatherStepData())
          }
          else if(ref === 'step6'){
            this.$refs['step6'].submitRequest()
          }
          return this.$refs[ref].validate();
        },
        handleCompleteGameFromFooter() {
          if (this.$refs['step3']) {
            this.$refs['step3'].completeGame();
          }
        },
        // Snapshot the customer screen timer as currently shown, for the draft
        // save and the review tab. Runs on completion and whenever the
        // objectives tab is left, so every path to the notes tab captures it.
        captureDisplayTimer() {
          if (this.gt_settings.enable_in_room_display && this.$refs.displayPanel && this.$refs.displayPanel.showTimer) {
            this.displayTimerValue = this.$refs.displayPanel.getTimerValue()
            return { display_timer_value: this.displayTimerValue }
          }
          return {}
        },
        async handleCompleteGame(endTime) {
          // Save puzzle data and stop timer
          await this.$refs['step3'].setDraft(this.captureDisplayTimer());
          this.$refs['step3'].stopTimer();

          // Pause display panel timer and save hint log if in display mode
          if (this.gt_settings.enable_in_room_display && this.$refs.displayPanel) {
            this.$refs.displayPanel.pauseTimer()
            this.$refs.displayPanel.markCompleted()
            await this.saveHintLog()
          }

          // Set optional fields for notes step
          this.$refs['step4'].setOptionalFields(this.optional_fields);

          // Calculate the correct notes step index based on enabled tabs
          // Tabs: 0=Start, 1=Booking, 2=Players, 3=Puzzles (if enabled), 4=Notes
          let notesStepIndex = this.hasFieldEnabled(1) ? 4 : 3;

          // Mark previous tabs as checked so navigation works
          for (let i = 0; i <= notesStepIndex; i++) {
            if (this.$refs['wiz'].tabs[i]) {
              this.$refs['wiz'].tabs[i].checked = true;
            }
          }

          // Navigate to notes step
          this.$refs['wiz'].tabIndex(notesStepIndex);

          // Populate the notes step and set the auto-filled end time
          this.$nextTick(() => {
            this.$refs['step4'].populateDraft();
            this.$refs['step4'].setAutoEndTime(endTime);
          });

          // Show success notification
          this.$notify({
            type: 'success',
            message: `Event completed! End time recorded: ${endTime.toLocaleTimeString()}`,
            icon: 'tim-icons icon-check-2'
          });
        },
        hasFieldEnabled(fieldId){
          for(let n in this.optional_fields){
            if(this.optional_fields[n].field_id === fieldId && this.optional_fields[n].is_enabled){
              return true
            }
          }
          return false
        },

        // --- Mirror display ---
        onMirrorIframeLoad() {
          this.$nextTick(() => {
            this.updateMirrorScale()
            this.mirrorReady = true
          })
        },
        updateMirrorScale() {
          if (this.$refs.mirrorBody && this.$refs.mirrorBody.clientWidth > 0) {
            this.mirrorScale = this.$refs.mirrorBody.clientWidth / 1920
          }
        },

        // --- In-room display methods ---
        connectWebSocket(gameId) {
          if (this.isDestroying) return
          if (this.socket) {
            this.socket.onclose = null
            this.socket.close()
            this.socket = null
          }
          let url = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production?gameId=' + gameId
          this.socket = new WebSocket(url)

          this.socket.onopen = () => {
            this.wsReconnectAttempts = 0
            // Send initial sync once connected
            if (this.$refs.displayPanel) {
              this.$refs.displayPanel.autoSync()
            }
          }

          this.socket.onclose = () => {
            this.socket = null
            if (!this.isDestroying) {
              // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
              let delay = Math.min(1000 * Math.pow(2, this.wsReconnectAttempts), 30000)
              this.wsReconnectAttempts++
              setTimeout(() => this.connectWebSocket(gameId), delay)
            }
          }

          this.socket.onerror = (error) => {
            console.error('WebSocket error:', error)
          }
        },
        disconnectWebSocket() {
          this.isDestroying = true
          if (this.socket) {
            this.socket.close()
            this.socket = null
          }
        },
        async fetchDisplayConfig(gameId) {
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let companyId = localStorage.getItem('companyId')
          let url = API_LOCATION + 'companies/' + companyId + '/games/' + gameId + '/clue-display-configurations'

          try {
            let response = await axios.get(url, config)
            this.displayConfig = response.data.configurations
          } catch (err) {
            console.log('Could not fetch display configuration:', err)
            this.displayConfig = {}
          }
        },
        handlePuzzleHintSent(hintData) {
          // Hint sent from PuzzleStep's inline hint panel
          // Update the hint counter on whichever panel shows it
          if (hintData.countAsHint) {
            if (this.gt_settings.display_mirror_view && this.$refs.displayPanelSecondary) {
              this.$refs.displayPanelSecondary.decrementHintCounter()
            } else if (this.$refs.displayPanel) {
              this.$refs.displayPanel.decrementHintCounter()
            }
          }
        },
        async saveHintLog() {
          if (!this.$refs.displayPanel) return

          let hintLog = this.$refs.displayPanel.getHintLog()
          if (!hintLog || hintLog.length <= 0) return

          let bookingInfo = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo')))
          if (!bookingInfo || !bookingInfo.booking || !bookingInfo.booking.id) return

          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let companyId = localStorage.getItem('companyId')
          let companyGroupId = JSON.parse(localStorage.getItem('group')).id
          let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/clues'

          let data = {
            clues: hintLog,
            booking_slot_id: bookingInfo.booking.id
          }

          try {
            await axios.post(url, data, config)
          } catch (err) {
            console.log('Failed to save hint log:', err)
          }
        },
        onTabChange(oldTab, newTab) {
          let oldIndex = this.$refs.wiz.tabs.indexOf(oldTab)
          let newIndex = this.$refs.wiz.tabs.indexOf(newTab)

          // Save step data to localStorage when navigating away
          let groupTabIndex = 2
          let notesTabIndex = this.hasFieldEnabled(1) ? 4 : 3
          let photosTabIndex = this.hasFieldEnabled(2) ? notesTabIndex + 1 : -1

          if (oldIndex === groupTabIndex && this.$refs['step2']) {
            this.pendingDraftSave = this.$refs['step2'].saveDraftToBackend()
          }
          if (oldIndex === notesTabIndex && this.$refs['step4']) {
            this.pendingDraftSave = this.$refs['step4'].saveDraftToBackend()
          }
          if (oldIndex === photosTabIndex && this.$refs['step5']) {
            this.$refs['step5'].saveToLocalDraft()
          }
          // Back on Start: refresh the list so the selected card and any
          // later re-select reflect what the server holds now
          if (newIndex === 0 && this.$refs['step0']) {
            this.refreshDraftList()
          }

          // Objectives tab is always at index 3
          this.isObjectivesStep = (newIndex === 3 && this.hasFieldEnabled(1))

          // Sync display panel width with the tab switch (not before)
          if (newIndex === 3 && this.hasFieldEnabled(1) && this.gt_settings.enable_in_room_display) {
            this.showDisplayPanel = true
          } else if (this.showDisplayPanel) {
            this.showDisplayPanel = false
          }
        },
        async awaitPendingDraftSave() {
          const save = this.pendingDraftSave
          if (!save) return
          try {
            await save
          } catch (err) {
            console.log('Background draft save failed:', err)
          }
          // Only clear if no newer save was queued while this one was awaited
          if (this.pendingDraftSave === save) {
            this.pendingDraftSave = null
          }
        },
        async refreshDraftList() {
          try {
            await this.awaitPendingDraftSave()
            if (this.$refs['step0']) {
              await this.$refs['step0'].getDrafts()
            }
          } catch (err) {
            console.log('Draft list refresh failed:', err)
          }
        },
        async onDraftClicked(draft) {
          if (!this.$refs['step0']) return
          if (this.selectingDraft) {
            // Latest click wins: remember it and run it once the in-flight
            // selection settles, instead of dropping it
            this.queuedDraftClick = draft
            return
          }
          this.selectingDraft = true
          try {
            await this.awaitPendingDraftSave()
            let fresh = await this.$refs['step0'].refreshSelection(draft.id)
            if (fresh) {
              this.onDraftSelected(fresh)
            }
          } catch (err) {
            console.log('Draft selection failed:', err)
          } finally {
            this.selectingDraft = false
          }
          let queued = this.queuedDraftClick
          this.queuedDraftClick = null
          if (queued && String(queued.id) !== String(draft.id)) {
            await this.onDraftClicked(queued)
          }
        },
        onDraftSelected(draft) {
          if (!draft || !this.$refs['wiz']) return
          this.lastSelectedDraft = draft
          // Reset child step state so stale data from a previous draft
          // doesn't carry over (components stay mounted via v-show)
          if(this.$refs['step2']){
            this.$refs['step2'].resetState()
          }
          if(this.$refs['step3']){
            this.$refs['step3'].resetState()
          }
          this.$refs['step4'].resetState()
          if(this.hasFieldEnabled(2) && this.$refs['step5']){
            this.$refs['step5'].resetState()
          }
          this.displayTimerValue = null
          localStorage.removeItem(this.draftKey('photo'))
          localStorage.removeItem(this.draftKey('preNotes'))
          localStorage.removeItem(this.draftKey('postNotes'))
          localStorage.removeItem(this.draftKey('puzzles'))
          // Reset all tabs except Start (index 0) so stale checked
          // state from a previous draft doesn't carry over
          for (let i = 1; i < this.$refs['wiz'].tabs.length; i++) {
            this.$refs['wiz'].tabs[i].checked = false
          }
          // Mark tabs as checked based on how far this draft progressed.
          // current_step tracks the last completed step (1-indexed), so
          // use current_step (not current_step-1) to also unlock the next
          // step the user should work on.
          if (draft.current_step && draft.current_step > 1) {
            let maxTab = Math.min(draft.current_step, this.$refs['wiz'].tabs.length - 1)
            for (let i = 0; i <= maxTab; i++) {
              if (this.$refs['wiz'].tabs[i]) {
                this.$refs['wiz'].tabs[i].checked = true
              }
            }
          }
        },
        onStepValidated(validated, model) {
          this.wizardModel = { ...this.wizardModel, ...model };
          window.scrollTo(0,0)
        },
        onGetDrafts(drafts){
          if(drafts.length <= 0){
            this.show_next_button = false
          } else {
            this.show_next_button = true
          }
          this.cleanOrphanedDraftKeys(drafts)
        },
        cleanOrphanedDraftKeys(drafts) {
          const activeDraftIds = new Set(drafts.map(d => String(d.id)))
          const suffixes = ['draft', 'photo', 'preNotes', 'postNotes', 'puzzles', 'bookingInfo']
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i)
            for (const suffix of suffixes) {
              const prefix = suffix + '_'
              if (key && key.startsWith(prefix)) {
                const draftId = key.slice(prefix.length)
                if (draftId && draftId !== 'NONE' && !activeDraftIds.has(draftId)) {
                  localStorage.removeItem(key)
                }
              }
            }
          }
        },
        async wizardComplete() {
          if (this.submitted) {
            this.$router.push({ path: '/games/history/instance', query: { id: this.completedInstanceId }})
            return
          }
          this.submitted = true
          this.loading = true

          // Save hint log and reset customer screen before final submission
          if (this.gt_settings.enable_in_room_display) {
            await this.saveHintLog()
            if (this.$refs.displayPanel) {
              this.$refs.displayPanel.resetTimer()
              this.$refs.displayPanel.resetHints()
              this.$refs.displayPanel.clearHints()
            }
            this.disconnectWebSocket()
          }

          let companyId = localStorage.getItem('companyId')
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId
            + '/games/instances/' + this.gameInstanceId + '/drafts'
          let data = {}
          const notesStep = this.$refs['step4']
          if (notesStep && notesStep.hostFieldEnabled && notesStep.hostId !== null && notesStep.hostId !== undefined) {
            data.game_master_id = notesStep.hostId
          }
          let gameInstanceId = this.gameInstanceId
          try {
            await axios.post(url, data, config)

            // Check if photo should be emailed to group
            let photo = JSON.parse(localStorage.getItem(this.draftKey('photo')))

            if(photo && photo.email_to_group && photo.photo_id && photo.photo_link_id){
              let preNotes = JSON.parse(localStorage.getItem(this.draftKey('preNotes')))
              if(preNotes && preNotes.users){
                let confirmedUsers = preNotes.users.filter(u => u.confirmed)
                await this.emailToGroup(photo.photo_link_id, confirmedUsers, photo.photo_id)
              }
            }

            this.loading = false
            this.completedInstanceId = gameInstanceId
            // Clear the ?draft= URL param now that this tracker is submitted, so
            // browser back/refresh doesn't resurrect a finalized draft on the
            // tracker page and silently let it be edited. Does not touch
            // gameInstanceId on the component (still needed for the history
            // redirect below).
            let finishedUrl = new URL(window.location)
            finishedUrl.searchParams.delete('draft')
            window.history.replaceState(history.state, '', finishedUrl)
            let swalResponse = await swal({
              title: 'Good job!',
              text: "You successfully filled out the event form!",
              type: 'success',
              confirmButtonColor: '#1d8cf8',
              confirmButtonText: 'OK'
            })
            if(swalResponse.value == true){
              this.$router.push({ path: '/games/history/instance', query: { id: gameInstanceId }})
            }
          } catch (err) {
            this.loading = false
            if(err.response && err.response.status == 401){
              await swal({
                title: 'Error',
                html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                type: 'error',
              })
              this.$router.push('/')
            } else if (err.response && err.response.status == 400) {
              this.submitted = false
              await this.showAlert({ title: 'Host not available', message: 'The selected host is not available for this location. Pick another host and submit again.', variant: 'warning' })
            }  else if (err.response && err.response.status == 502) {
              this.submitted = false
              swal({
                title: 'Ooops!',
                text: "There were some issues with your submission. Please check your form and try again!",
                type: 'warning',
                confirmButtonColor: '#1d8cf8',
                confirmButtonText: 'OK'
              })
            } else {
              this.submitted = false
            }
          }

        },
        async getAllDrafts(){
          this.loading = true
          let token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let companyId = localStorage.getItem('companyId')
          let companyGroupId = JSON.parse(localStorage.getItem('group')).id
          let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-instances/drafts/all'

          axios.get(url, config)
            .then(response => {
              this.user_drafts = response.data.drafts
              this.company_users = response.data.company_users
              for(let n in this.company_users){
                if(this.company_users[n].first_name === null){
                  this.company_users[n].first_name = ''
                }
                if(this.company_users[n].last_name === null){
                  this.company_users[n].last_name = ''
                }
              }
              this.view_user_drafts = true
              this.loading = false
            })
            .catch(err => {
              this.loading = false
              if(err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else {
                swal('Error!', 'Something went wrong', 'error')
              }
            })
        },
        deleteDraftPrompt(d){
          swal({
            title: 'Are you sure?',
            text: "The draft will be permanently deleted.",
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete'
          }).then((response)=> {
            if(response.value == true){
              this.deleteDraft(d)
            }
          })
        },
        async deleteDraft(d){
          this.loading = true
          let companyId = localStorage.getItem('companyId')

          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + d.id + '/drafts'
          axios.delete(url, config)
            .then(response => {
              this.loading = false
              this.selectedDraft = ''
              localStorage.removeItem(this.draftKey('draft'))
              if (this.gameInstanceId && String(this.gameInstanceId) === String(d.id)) {
                this.updateGameInstanceId(null)
              }
              swal({
                title: 'Success',
                text: "The draft has been deleted.",
                type: 'success'
              })
              this.getAllDrafts()
            })
            .catch(err => {
              this.loading = false
              if(err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else {
                swal('Error!', 'Something went wrong', 'error')
              }
            })
        },
        async createNewDraft(){
          localStorage.removeItem(this.draftKey('draft'))
          localStorage.removeItem(this.draftKey('photo'))
          localStorage.removeItem(this.draftKey('preNotes'))
          localStorage.removeItem(this.draftKey('postNotes'))
          localStorage.removeItem(this.draftKey('puzzles'))
          localStorage.removeItem(this.draftKey('bookingInfo'))
          this.$refs['step1'].clearSelection()
          if(this.$refs['step2']){
            this.$refs['step2'].resetState()
          }
          if(this.$refs['step3']){
            this.$refs['step3'].resetState()
          }
          this.$refs['step4'].resetState()
          if(this.hasFieldEnabled(2) && this.$refs['step5']){
            this.$refs['step5'].resetState()
          }
          // Reset progress bar — only Start + Booking should be accessible
          for (let i = 2; i < this.$refs['wiz'].tabs.length; i++) {
            this.$refs['wiz'].tabs[i].checked = false
          }

          let companyId = localStorage.getItem('companyId')

          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId + '/games/instances/drafts'
          let data = {}
          axios.post(url, data, config)
            .then(response => {
              this.updateGameInstanceId(response.data)
              // Point the start form at the new draft so the list refresh
              // below confirms it instead of re-emitting the previously
              // selected draft's id
              if (this.$refs['step0']) {
                this.$refs['step0'].selectedDraft = { id: response.data }
                this.$refs['step0'].error = false
              }
              this.show_next_button = true
              this.$refs['step1'].getBookings()
              this.$refs['step0'].getDrafts()
              this.$refs['wiz'].tabIndex(1);
            })
            .catch(err => {
              this.loading = false
              if(err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else {
                swal('Error!', 'Something went wrong', 'error')
              }
            })
        },
        async getGTSettings(){
          this.loading = true
          const token = await this.getAuthToken()
          const config = { headers: { Authorization: token } }
          const companyId = localStorage.getItem('companyId')
          const companyGroupId = JSON.parse(localStorage.getItem('group')).id
          const url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/game-tracker-settings'
          axios.get(url, config)
            .then(response => {
              this.gt_settings = response.data.game_tracker_settings
              this.gt_settings.include_review_step = true
              this.gt_settings.display_timer = this.gt_settings.display_timer == null ? true : Boolean(this.gt_settings.display_timer)
              this.gt_settings.display_background_music = this.gt_settings.display_background_music == null ? true : Boolean(this.gt_settings.display_background_music)
              this.gt_settings.display_custom_interaction = this.gt_settings.display_custom_interaction == null ? true : Boolean(this.gt_settings.display_custom_interaction)
              this.gt_settings.display_hint_counter = this.gt_settings.display_hint_counter == null ? true : Boolean(this.gt_settings.display_hint_counter)
              this.gt_settings.display_mirror_view = this.gt_settings.display_mirror_view == null ? false : Boolean(this.gt_settings.display_mirror_view)
              this.gt_settings.show_complete_button = this.gt_settings.show_complete_button == null ? true : Boolean(this.gt_settings.show_complete_button)
              this.gt_settings.enable_start_game_button = this.gt_settings.enable_start_game_button == null ? false : Boolean(this.gt_settings.enable_start_game_button)
              this.gt_settings.enable_complete_game_button = this.gt_settings.enable_complete_game_button == null ? false : Boolean(this.gt_settings.enable_complete_game_button)
              this.optional_fields = response.data.fields
              this.loading = false
              // Re-mark tabs after conditional tabs (Objectives, Photos) render.
              // Only re-mark checked state — don't call onDraftSelected which
              // also clears localStorage and resets child step state.
              this.$nextTick(() => {
                let draft = this.lastSelectedDraft
                if (draft && draft.current_step && draft.current_step > 1) {
                  let maxTab = Math.min(draft.current_step, this.$refs['wiz'].tabs.length - 1)
                  for (let i = 0; i <= maxTab; i++) {
                    if (this.$refs['wiz'].tabs[i]) {
                      this.$refs['wiz'].tabs[i].checked = true
                    }
                  }
                }
              })
            })
            .catch(err => {
              this.loading = false
              if(err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else {
                swal('Error!', 'Something went wrong', 'error')
              }
            })
        },
        getLocalDate(d){
          return formatGroupDate(d)
        },
        getFormattedDate(d){
          return formattedDate(this.date_format, d)
        },
        getTime(d) {
          return time(this.time_format, d)
        },
        draftKey(key) {
          if (!this.gameInstanceId) {
            return key + '_NONE'
          }
          return key + '_' + this.gameInstanceId
        },
        gatherStepData() {
          let photoStep = this.$refs['step5']
          let photoData = null
          if (photoStep) {
            photoData = {
              ...photoStep.model,
              email_to_group: photoStep.email_to_group,
              photo_id: photoStep.photo_id,
              photo_link_id: photoStep.photo_link_id
            }
          }
          return {
            bookingInfo: this.$refs['step1'] ? this.$refs['step1'].model : null,
            preNotes: this.$refs['step2'] ? this.$refs['step2'].model : null,
            puzzles: this.$refs['step3'] ? this.$refs['step3'].model.puzzles : [],
            postNotes: this.$refs['step4'] ? this.$refs['step4'].model : null,
            pacing: this.$refs['step3'] && this.$refs['step3'].getPacingContext ? this.$refs['step3'].getPacingContext() : null,
            photo: photoData,
            displayTimer: this.displayTimerValue,
            hostName: this.$refs['step4'] ? this.$refs['step4'].hostName : '',
            draft: this.lastSelectedDraft
          }
        },
        updateGameInstanceId(id) {
          this.gameInstanceId = id != null ? String(id) : null
          // Use history.replaceState directly to avoid Vue Router's async
          // navigation queue — rapid draft toggling can cause NavigationDuplicated
          // errors that silently drop URL updates
          let url = new URL(window.location)
          if (id != null) {
            url.searchParams.set('draft', String(id))
          } else {
            url.searchParams.delete('draft')
          }
          window.history.replaceState(history.state, '', url)
        },
        async emailToGroup(photo_link_id, players, photo_id){
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let companyId = localStorage.getItem('companyId')
          let companyGroupId = JSON.parse(localStorage.getItem('group')).id
          let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/'+ photo_id + '/email-group'

          let data = {
            emails: players,
            link_type: 1,
            link_id: photo_link_id,
            // Auto-send on submit: never block on size, attach what fits and link the rest.
            allow_links: true
          }

          return axios.post(url, data, config)
            .then(response => {
              // Email sent successfully
            }).catch((err) => {
              if(err.response.status == 401){
                this.$router.push('/forbidden')
              } else if(isNoRecipientsError(err)){
                this.showAlert({ title: 'Photo saved, but no one to email', message: NO_RECIPIENTS_MESSAGE, variant: 'warning' })
              } else {
                this.showAlert({
                  title: 'Photo saved, but email failed to send',
                  message: 'You can resend it from the photos page.',
                  variant: 'warning'
                })
              }
            })
        },
      },
    beforeDestroy() {
        this.disconnectWebSocket()
        window.removeEventListener('resize', this.updateMirrorScale)
    },
    mounted() {
        // Restore gameInstanceId from URL query param (survives page refresh)
        if (this.$route.query.draft) {
          this.gameInstanceId = String(this.$route.query.draft)
        }
        this.getGTSettings()
        window.addEventListener('resize', this.updateMirrorScale)
    },
  }
</script>

<style scoped>
</style>

<!-- Unscoped: shared game-tracker styles + wizard stepper + display layout -->
<style>
@import './game-tracker.css';

.complete-game-footer-btn.btn.btn-primary {
  background: linear-gradient(to bottom left, #1d8cf8, #3358f4, #1d8cf8) !important;
  background-size: 210% 210% !important;
  box-shadow: 0 4px 15px rgba(29, 140, 248, 0.4);
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.3s ease;
  min-height: 44px;
  font-size: 13px;
  border-radius: 6px;
}

.complete-game-footer-btn.btn.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(29, 140, 248, 0.5);
}

@media (max-width: 575.98px) {
  .complete-game-footer-btn.btn.btn-primary {
    font-size: 10px;
    min-height: 28px;
    padding: 4px 8px !important;
  }
  .complete-game-footer-btn.btn.btn-primary .tim-icons {
    display: none;
  }
}
/* Base wizard card layout — pin footer, scroll body */
.game-tracker .card-wizard {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  min-height: 500px;
}

.game-tracker .card-wizard form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Compact modern pill stepper */
.game-tracker .card-wizard .card-header {
  padding: 0;
  flex-shrink: 0;
}

.game-tracker .card-wizard .wizard-navigation {
  margin: 0 auto;
}

/* Hide the progress track line */
.game-tracker .card-wizard .progress-with-circle {
  display: none;
}

/* Convert nav-pills from track bar to flex pill row */
.game-tracker .card-wizard .nav-pills {
  height: auto;
  background-color: transparent;
  border-radius: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
}

.game-tracker .card-wizard .nav-pills .nav-item.wizard-tab-link {
  width: auto !important;
  position: static;
}

/* Convert nav-links from circles to flat compact pills */
.game-tracker .card-wizard .nav-pills .nav-item .nav-link {
  position: relative;
  height: auto;
  width: auto;
  min-width: auto;
  top: 0;
  left: 0;
  transform: none;
  border-radius: 4px !important;
  background-color: rgba(255, 255, 255, 0.08) !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  transition: all 0.15s ease;
  border: 1px solid transparent;
  color: rgba(255, 255, 255, 0.5) !important;
}

.game-tracker .card-wizard .nav-pills .nav-item .nav-link:hover,
.game-tracker .card-wizard .nav-pills .nav-item .nav-link:focus {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

.game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked:hover,
.game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked:focus {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.game-tracker .card-wizard .nav-pills .nav-item .nav-link.active,
.game-tracker .card-wizard .nav-pills .nav-item .nav-link.active:focus,
.game-tracker .card-wizard .nav-pills .nav-item .nav-link.active:hover {
  background: rgba(225, 78, 202, 0.15) !important;
  border-color: #e14eca;
  color: #e14eca !important;
}

/* Make label inline instead of absolutely positioned below circle */
.game-tracker .card-wizard .nav-pills .nav-item .nav-link p {
  position: static;
  top: auto;
  left: auto;
  transform: none;
  font-size: 11px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
  color: inherit;
}

/* Icons smaller and inline */
.game-tracker .card-wizard .wizard-navigation .nav-link i {
  font-size: 13px;
  line-height: 1;
}

/* Light mode: wizard pill overrides */
.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link {
  background-color: rgba(0, 0, 0, 0.04) !important;
  color: rgba(0, 0, 0, 0.45) !important;
}

.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link:hover,
.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link:focus {
  background-color: rgba(0, 0, 0, 0.08) !important;
}

.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked {
  background-color: rgba(0, 0, 0, 0.06) !important;
  color: #344675 !important;
}

.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked:hover,
.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.checked:focus {
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.active,
.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.active:focus,
.white-content .game-tracker .card-wizard .nav-pills .nav-item .nav-link.active:hover {
  background: rgba(225, 78, 202, 0.15) !important;
  border-color: #e14eca;
  color: #e14eca !important;
}

/* Card body: scrollable, fills remaining space */
.game-tracker .card-wizard .card-body {
  margin-top: 8px;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: none;
}

.game-tracker .card-wizard .card-body::-webkit-scrollbar {
  display: none;
}

.game-tracker .card-wizard .tab-content {
  padding: 10px 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.game-tracker .card-wizard .tab-content .tab-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.gt-display-row {
  flex: 1;
  min-height: 0;
  height: 0;
}

.gt-display-row > .gt-sidebar {
  overflow-y: auto;
  max-height: 100%;
  scrollbar-width: none;
}

.gt-display-row > .gt-sidebar::-webkit-scrollbar {
  display: none;
}

.gt-display-row > .col-lg-9,
.gt-display-row > .col-lg-6 {
  display: flex;
  flex-direction: column;
  max-height: 100%;
}

.gt-display-row > .gt-mirror-col {
  overflow-y: auto;
  max-height: 100%;
  scrollbar-width: none;
}

.gt-display-row > .gt-mirror-col::-webkit-scrollbar {
  display: none;
}

/* Card footer: match card-body background, close the bottom */
.game-tracker .card-wizard .card-footer {
  background-color: #222a42 !important;
  border-top: none;
  margin-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: 0.4285rem;
  border-bottom-right-radius: 0.4285rem;
  flex-shrink: 0;
}

.white-content .game-tracker .card-wizard .card-footer {
  background-color: #fff !important;
}

/* ========================================
   Display mode sidebar
   ======================================== */
.gt-sidebar {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding-right: 20px;
}

.white-content .gt-sidebar {
  border-right-color: rgba(0, 0, 0, 0.08);
}

/* ========================================
   Start step action buttons
   ======================================== */
.start-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.start-actions__new {
  background: linear-gradient(to bottom left, #1d8cf8, #3358f4, #1d8cf8) !important;
  background-size: 210% 210% !important;
  box-shadow: 0 4px 15px rgba(29, 140, 248, 0.4);
  border: none !important;
  font-weight: 600;
  padding: 8px 24px !important;
  transition: all 0.3s ease;
}

.start-actions__new:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(29, 140, 248, 0.5);
}

.start-actions__secondary {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 13px;
  padding: 5px 10px !important;
  text-transform: none !important;
}

.start-actions__secondary:hover {
  color: #ffffff !important;
}

.white-content .start-actions__secondary {
  color: rgba(0, 0, 0, 0.4) !important;
}

.white-content .start-actions__secondary:hover {
  color: #344675 !important;
}

/* ========================================
   Draft list (shared with StartNewForm)
   ======================================== */
.game-tracker .draft-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.game-tracker .draft-item {
  display: flex;
  align-items: center;
  gap: 14px;
}

.game-tracker .draft-item__img {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.game-tracker .draft-item__img-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.25);
  font-size: 18px;
}

.game-tracker .draft-item__content {
  flex: 1;
  min-width: 0;
}

.game-tracker .draft-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.game-tracker .draft-item__name {
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.game-tracker .draft-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.game-tracker .draft-item__action-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1;
  transition: color 0.15s ease;
}

.game-tracker .draft-item__action-btn:hover {
  color: #1d8cf8;
}

.game-tracker .draft-item__action-btn--save:hover {
  color: #00bf9a;
}

.game-tracker .draft-item__delete {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.game-tracker .draft-item__delete:hover {
  color: #fd5d93;
}

.game-tracker .draft-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.game-tracker .draft-item__meta-tag {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.game-tracker .draft-item__meta-tag i {
  font-size: 11px;
}

.game-tracker .draft-item__gm {
  margin-top: 6px;
}

/* Light mode — sub-element overrides (base handled by gt-list-item) */
.white-content .game-tracker .draft-item__img {
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .game-tracker .draft-item__img-placeholder {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.2);
}

.white-content .game-tracker .draft-item__name {
  color: #344675;
}

.white-content .game-tracker .draft-item__meta-tag {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .game-tracker .draft-item__delete {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .game-tracker .draft-item__delete:hover {
  color: #fd5d93;
}

.white-content .game-tracker .draft-item__action-btn {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .game-tracker .draft-item__action-btn:hover {
  color: #1d8cf8;
}

/* Mirror display panel */
.mirror-display-panel {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.3);
}

.mirror-display-panel__header {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.mirror-display-panel__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
}

.mirror-display-panel__body {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
  border-radius: 0 0 6px 6px;
}

.mirror-display-panel__loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
}

.mirror-display-panel__loading .tim-icons {
  font-size: 24px;
  animation: pulse-fade 1.5s ease-in-out infinite;
}

@keyframes pulse-fade {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.7; }
}

.mirror-display-panel__iframe {
  position: absolute;
  top: 0;
  left: 0;
  border: none;
}

.white-content .mirror-display-panel {
  border-color: rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.white-content .mirror-display-panel__header {
  background: rgba(0, 0, 0, 0.03);
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.white-content .mirror-display-panel__label {
  color: rgba(0, 0, 0, 0.5);
}

.white-content .mirror-display-panel__loading {
  color: rgba(0, 0, 0, 0.3);
}
</style>
